import Vue from 'vue';
import _ from 'lodash';
import {
  customersSearch,
  customersSimpleSearch,
  customerProfile,
  customerOrders,
  customerObjects,
  setCustomerBlocking,
  customerDelete,
} from '@/api/customers';
import {
  SET_CUSTOMERS,
  SET_SIMPLE_CUSTOMERS,
  SET_CUSTOMER,
  SET_CUSTOMER_ORDERS,
  SET_CUSTOMER_OBJECTS,
  SET_CUSTOMER_BLOCKING,
} from './mutation-types';

export default {
  state: {
    data: {
      payload: [],
      total: null,
    },
    simple: {
      payload: [],
      total: null,
    },
    customers: [],
    orders: {},
    objects: {},
  },
  getters: {
    customers: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return state.customers.find((obj) => parseInt(obj.id, 10) === parseInt(id, 10)) || null;
      }
      return state.customers;
    },
    orders: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.orders, id) || null;
      }
      return state.orders;
    },
    objects: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.objects, id) || null;
      }
      return state.objects;
    },
  },
  actions: {
    ACT_FETCH_CUSTOMERS: async ({ commit, dispatch }, {
      offset,
      limit,
      search,
      orderBy,
    }) => {
      dispatch('wait/start', 'fetch.customers', { root: true });
      return customersSearch({
        offset,
        limit,
        search,
        orderBy,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        commit(SET_CUSTOMERS, {
          data,
          total,
        });
      }).finally(() => {
        dispatch('wait/end', 'fetch.customers', { root: true });
      });
    },
    ACT_SIMPLE_FETCH_CUSTOMERS: async ({
      commit,
      dispatch,
      rootGetters: getters,
      state,
    }, { query }) => {
      if (getters['wait/is']('fetch.customers.simple')) {
        return Promise.reject();
      }
      dispatch('wait/start', 'fetch.customers.simple', { root: true });
      const offset = -20;
      let referenceId = null;
      if (state.simple.payload.length) {
        const last = _.last(state.simple.payload);
        referenceId = _.get(last, 'id') || null;
      }
      return customersSimpleSearch({
        referenceId,
        offset,
        registered: 1,
        verified: 1,
        search: query,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        commit(SET_SIMPLE_CUSTOMERS, {
          data,
          total,
        });
      }).finally(() => {
        dispatch('wait/end', 'fetch.customers.simple', { root: true });
      });
    },
    ACT_CLEAR_SIMPLE_CUSTOMERS: async ({ commit }) => {
      commit(SET_SIMPLE_CUSTOMERS, {
        data: [],
        total: null,
      });
      return Promise.resolve();
    },
    ACT_FETCH_CUSTOMER: async ({ commit, dispatch }, id) => {
      dispatch('wait/start', `fetch.customer.${id}`, { root: true });
      return customerProfile(id).then((response) => {
        if (response.data) {
          commit(SET_CUSTOMER, {
            id,
            data: response.data,
          });
          return Promise.resolve(response.data);
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `fetch.customer.${id}`, { root: true });
      });
    },
    ACT_FETCH_CUSTOMER_ORDERS: async ({ commit, dispatch }, {
      id,
      limit,
      offset,
      orderBy,
    }) => {
      dispatch('wait/start', `fetch.customer.orders.${id}`, { root: true });
      return customerOrders(id, {
        offset,
        limit,
        orderBy,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        if (data) {
          commit(SET_CUSTOMER_ORDERS, {
            id,
            data: data.map((order) => ({
              ...order,
              name: `${_.get(order, 'firstServiceCategory.abbreviation')}-${order.id}`,
            })),
            total,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `fetch.customer.orders.${id}`, { root: true });
      });
    },
    ACT_FETCH_CUSTOMER_OBJECTS: async ({ commit, dispatch }, {
      id,
      limit,
      offset,
      orderBy,
    }) => {
      dispatch('wait/start', `fetch.customer.objects.${id}`, { root: true });
      return customerObjects(id, {
        offset,
        limit,
        orderBy,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        if (data) {
          commit(SET_CUSTOMER_OBJECTS, {
            id,
            data,
            total,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `fetch.customer.objects.${id}`, { root: true });
      });
    },
    ACT_SET_CUSTOMER_BLOCKING: async ({ commit, dispatch }, { id, reason }) => {
      dispatch('wait/start', `block.customer.${id}`, { root: true });
      return setCustomerBlocking(id, reason).then((response) => {
        if (response.status === 200) {
          commit(SET_CUSTOMER_BLOCKING, {
            id,
            reason,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `block.customer.${id}`, { root: true });
      });
    },
    ACT_DELETE_CUSTOMER: async ({ dispatch }, id) => {
      dispatch('wait/start', `delete.customer.${id}`, { root: true });
      return customerDelete(id).then((response) => {
        if (response.status === 200) {
          return Promise.resolve();
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `delete.customer.${id}`, { root: true });
      });
    },
  },
  mutations: {
    [SET_CUSTOMERS](state, { data, total }) {
      _.set(state, 'data.payload', data);
      _.set(state, 'data.total', total);
    },
    [SET_CUSTOMER](state, { id, data }) {
      const payload = {
        ...data,
        id,
      };
      const foundIndex = _.findIndex(
        state.customers,
        (obj) => parseInt(obj.id, 10) === parseInt(id, 10),
      );
      if (foundIndex > -1) {
        Vue.set(state.customers, foundIndex, payload);
      } else {
        state.customers.push(payload);
      }
    },
    [SET_CUSTOMER_ORDERS](state, { id, data, total }) {
      Vue.set(state.orders, id, {
        data,
        total,
      });
    },
    [SET_CUSTOMER_OBJECTS](state, { id, data, total }) {
      Vue.set(state.objects, id, {
        data,
        total,
      });
    },
    [SET_CUSTOMER_BLOCKING](state, { id, reason }) {
      state.customers = state.customers.map((obj) => {
        const data = obj;
        if (parseInt(obj.id, 10) === parseInt(id, 10)) {
          data.blockingReason = reason;
        }
        return data;
      });
    },
    [SET_SIMPLE_CUSTOMERS](state, { data, total }) {
      if (data.length && total !== null) {
        state.simple = {
          payload: _.unionBy(state.simple.payload, data, (obj) => obj.id),
          total,
        };
      } else {
        state.simple = {
          payload: data,
          total,
        };
      }
    },
  },
  namespaced: true,
};
